import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { DOCUMENT } from '@angular/common';

import { filter, interval, Subscription, tap } from 'rxjs';

import { ConnectionAlertService, LoaderService, ServerTimeService } from '@tablesready/common-ui';
import { FAVICON_ICONS } from '@tablesready/common-ui';
import { SocketsService } from './services';
import { AppVariables, APP_VARIABLES_TOKEN, AppVariablesService } from '@tablesready/frontend-variables';
import { FrontendKeyboardService } from '@tablesready/frontend-keyboard';

@Component({
  selector: 'tablesready-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnInit, OnDestroy {
  path: string;
  private readonly socketHealthCheckIntervalMs = 15000;
  private subscriptions = new Subscription();

  constructor(
    private readonly loaderService: LoaderService,
    private readonly connectionAlertService: ConnectionAlertService,
    private readonly socketService: SocketsService,
    private readonly appVariablesService: AppVariablesService,
    @Inject(APP_VARIABLES_TOKEN) private readonly appVariables: AppVariables,
    @Inject(DOCUMENT) private document: HTMLDocument,
    private readonly serverTimeService: ServerTimeService,
    private readonly keyboardService: FrontendKeyboardService,
  ) {
    loaderService.setLoading(true);
    // No analytics/RUM tracking on public guest pages — only strictly-necessary
    // processing runs here, so no cookie-consent banner is required.
    this.keyboardService.init();
  }

  ngOnInit(): void {
    this.document.addEventListener('visibilitychange', this.visibilityChangeListener);

    const connectSocketsSub = this.socketService.onConnectionError.subscribe(() => {
      this.connectionAlertService.show();
    });

    const authViaSocketsSub = this.socketService.onAuthenticated.subscribe(() => {
      // Fires on the real backend `authenticated` ack, so only a genuine reconnection reaches here
      // — the banner can't clear without a real recovery.
      this.connectionAlertService.dismiss();
    });

    this.path = this.appVariables.WHITELABEL_BRAND.toLowerCase();
    this.generateFaviconIcons(this.path);
    this.subscriptions.add(connectSocketsSub);
    this.subscriptions.add(authViaSocketsSub);

    this.socketService.connect();

    // Kiosks/status pages may stay open for days and never fire a visibility change, while a
    // server-initiated disconnect (e.g. the cached anon token expiring past its 24h TTL) kills the
    // socket in a way socket.io never retries. Poll and rebuild — createSocket() fetches a fresh
    // anon token when the cached one has expired. Skipped while offline: dialing would fail anyway,
    // and the first tick after connectivity returns recovers.
    const socketHealthCheckSub = interval(this.socketHealthCheckIntervalMs)
      .pipe(filter(() => navigator.onLine !== false && !this.socketService.isConnected()))
      .subscribe(() => this.socketService.reconnectIfNeeded());
    this.subscriptions.add(socketHealthCheckSub);

    const offsetTimeSubscription = this.serverTimeService.offset$
      .pipe(
        tap(offset => {
          // @ts-ignore
          Date.setOffset(offset);
        }),
      )
      .subscribe();
    this.subscriptions.add(offsetTimeSubscription);
  }

  generateFaviconIcons(path) {
    const head = this.document.getElementsByTagName('head')[0],
      fragment = this.document.createDocumentFragment(),
      links = [...FAVICON_ICONS];

    links.forEach((link) => {
      const tag = this.document.createElement("link");
      tag.setAttribute('rel', link.rel);
      tag.setAttribute('href', `./assets/img/${path}/${link.name}`);
      tag.setAttribute('sizes', link.sizes);
      tag.setAttribute('media', link.media);
      tag.setAttribute('color', link.color);
      fragment.appendChild(tag);
    });

    head.appendChild(fragment);
  }

  ngOnDestroy() {
    this.document.removeEventListener('visibilitychange', this.visibilityChangeListener);
    this.subscriptions.unsubscribe();
    this.socketService.disconnect();
  }

  private readonly visibilityChangeListener = (): void => {
    // Refocusing the tab is not itself a recovery: the banner is dismissed below only once the
    // socket is verified healthy (the old blanket alerts clear hid it while it was still down).
    if (!this.document.hidden) {
      this.appVariablesService.getAppVariables().subscribe();

      if (this.socketService.isConnected()) {
        // Socket healthy on refocus: a lingering connection banner (e.g. raised by the HTTP error
        // interceptor for a transient blip) is stale — this is the only dismissal such a banner
        // gets, since the `authenticated` ack only fires on a (re)connection.
        this.connectionAlertService.dismiss();
      } else {
        // Dead socket after a freeze: rebuild now rather than waiting for the health-check tick;
        // the resulting `authenticated` ack dismisses the banner.
        this.socketService.reconnectIfNeeded();
      }
    }
  };
}
